import { Controller } from "@hotwired/stimulus";

// The French pack is served by the application itself
// (public/tinymce/langs/fr-FR.js, from tinymce-i18n): no CDN, and the
// script-src 'self' of the CSP lets it in. English is TinyMCE's own.
// The same options are given to the editors of the `tinymce` helper
// (ApplicationHelper#tinymce_language_options).
const TINYMCE_LANGUAGES = { fr: "fr-FR" };

function tinymceLanguage() {
    const locale = document.querySelector('meta[name="current-locale"]')?.content;
    const language = TINYMCE_LANGUAGES[locale];
    if (!language) return {};
    return { language, language_url: `/tinymce/langs/${language}.js` };
}

export default class extends Controller {
    static targets = ["editor"];
    static values = {
        config: Object
    };

    connect() {
        // Prevent double initialization
        if (this.editorTarget.dataset.tinymceInitialized === "true") {
            return;
        }

        // Wait for TinyMCE to be loaded
        if (typeof tinymce === "undefined") {
            this.waitForTinyMCE();
        } else {
            this.initializeTinyMCE();
        }
    }

    waitForTinyMCE() {
        const maxAttempts = 50; // 5 seconds max wait time
        let attempts = 0;

        const checkTinyMCE = () => {
            if (typeof tinymce !== "undefined") {
                this.initializeTinyMCE();
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkTinyMCE, 100);
            } else {
                console.error("TinyMCE failed to load after 5 seconds");
            }
        };

        checkTinyMCE();
    }

    disconnect() {
        this.destroyTinyMCE();
    }

    initializeTinyMCE() {
        const editorId = this.editorTarget.id;

        // Remove existing instance if any
        if (tinymce.get(editorId)) {
            tinymce.get(editorId).remove();
        }

        // Default configuration
        const defaultConfig = {
            selector: `#${editorId}`,
            height: 400,
            min_height: 350,
            max_height: 600,
            autoresize_bottom_margin: 20,
            menubar: true,
            toolbar_mode: "wrap",
            license_key: "gpl",
            plugins: [
                "advlist",
                "anchor",
                "autolink",
                "autoresize",
                "charmap",
                "code",
                "codesample",
                "emoticons",
                "fullscreen",
                "help",
                "image",
                "insertdatetime",
                "link",
                "lists",
                "media",
                "preview",
                "searchreplace",
                "table",
                "visualblocks",
                "visualchars",
                "wordcount"
            ],
            toolbar: [
                "undo redo | styles blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor",
                "alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | removeformat",
                "link image media table | charmap emoticons | codesample | insertdatetime | preview fullscreen help"
            ],
            menu: {
                edit: {
                    title: "Edit",
                    items: "undo redo | cut copy paste | selectall | searchreplace"
                },
                view: {
                    title: "View",
                    items: "code | visualblocks | preview fullscreen"
                },
                insert: {
                    title: "Insert",
                    items: "image link media codesample inserttable | charmap emoticons | insertdatetime"
                },
                format: {
                    title: "Format",
                    items: "bold italic underline strikethrough | styles blocks fontfamily fontsize | align | forecolor backcolor | removeformat"
                },
                table: {
                    title: "Table",
                    items: "inserttable | tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol"
                },
                help: {
                    title: "Help",
                    items: "help"
                }
            },
            branding: false,
            resize: "both",
            statusbar: true,
            elementpath: true,
            promotion: false,
            remove_trailing_brs: true,
            convert_urls: false,
            relative_urls: false,
            remove_script_host: false,
            entity_encoding: "raw",
            font_family_formats: "Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Oswald=oswald; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; DM Sans='DM Sans',sans-serif",
            font_size_formats: "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
            block_formats: "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Preformatted=pre; Blockquote=blockquote",
            content_style: "body { font-family: 'DM Sans', Arial, sans-serif; font-size: 14px; line-height: 1.6; } img { max-width: 100%; height: auto; } table { border-collapse: collapse; width: 100%; } table td, table th { border: 1px solid #ddd; padding: 8px; } pre { background-color: #f4f4f4; padding: 10px; border-radius: 4px; }",
            image_advtab: true,
            image_caption: true,
            image_title: true,
            image_description: true,
            file_picker_types: "image media",
            link_title: true,
            link_target_list: false,
            default_link_target: "_blank",
            link_context_toolbar: true,
            paste_data_images: true,
            paste_as_text: false,
            paste_webkit_styles: "all",
            paste_merge_formats: true,
            smart_paste: true,
            setup: (editor) => {
                // Save content on various events to ensure textarea is updated;
                // a change is told to the form too (the error banner drops the
                // line of the text once it is written).
                editor.on("change", () => {
                    editor.save();
                    editor.getElement()?.dispatchEvent(new Event("change", { bubbles: true }));
                });
                editor.on("blur", () => {
                    editor.save();
                });
            }
        };

        // Merge with custom config if provided; the menus follow the page's
        // language (owner's decision Q11, 30/10/2026).
        const config = {
            ...(this.hasConfigValue ? { ...defaultConfig, ...this.configValue } : defaultConfig),
            ...tinymceLanguage(),
        };

        // Initialize TinyMCE
        tinymce.init(config).then(() => {
            this.editorTarget.dataset.tinymceInitialized = "true";
        });
    }

    destroyTinyMCE() {
        const editorId = this.editorTarget.id;
        const editor = tinymce.get(editorId);

        if (editor) {
            // Save content before destroying
            editor.save();
            editor.remove();
            this.editorTarget.dataset.tinymceInitialized = "false";
        }
    }
}
